// The launcher's choices, and how they map onto each flow's real inputs.
// Kept framework-free so it can be unit-tested.

export type FlowKind = "analyze" | "fix" | "fullstack" | "compare";
export type Auth = "bedrock" | "subscription";
export type Effort = "low" | "medium" | "high";

export interface FlowOption {
  kind: FlowKind;
  path: string;
  title: string;
  blurb: string;
  abable: boolean;
  can: string[];
  cannot: string[];
  // Still deployed (Onyx calls it, "Run again" on an old run reopens it), but not offered.
  hidden?: boolean;
}

const CANNOT_COMMON = [
  "Push to any branch or merge (later flow steps commit and open the PR)",
  "Use the AWS CLI, cloud metadata or credential files",
  "Touch CI config, Dockerfiles, IaC, lockfiles or env files",
];

export const FLOWS: FlowOption[] = [
  {
    kind: "analyze", path: "f/jira_bug_analysis_deep/main", title: "Analyze",
    blurb: "Investigate the ticket and open an analysis-only PR. No source edits.",
    abable: false,
    can: ["Read and search the whole repo", "Query symbols with Serena", "Write analysis.json"],
    cannot: ["Edit source files", ...CANNOT_COMMON],
  },
  {
    kind: "fix", path: "f/jira_bug_analysis_fix/main", title: "Fix",
    blurb: "Find the root cause, apply a fix, open a PR. CI verifies it.",
    abable: true,
    can: ["Read, search and edit files inside the clone", "Up to 30 changed files", "Run read-only shell commands"],
    cannot: CANNOT_COMMON,
  },
  {
    kind: "fullstack", path: "f/jira_bug_analysis_fix_fullstack/main", title: "Fullstack fix",
    blurb: "One agent across backend and frontend repos; two linked PRs.",
    abable: true,
    can: ["Edit both clones in one workspace", "Reason about the API contract across both sides"],
    cannot: CANNOT_COMMON,
  },
  {
    kind: "compare", path: "f/jira_bug_analysis_fix_compare/main", title: "Claude vs Codex",
    blurb: "Run the Claude and Codex fix flows in parallel and compare the diffs.",
    abable: false,
    hidden: true, // 2026-10-10: Codex's OpenAI key is invalid; @majlis now starts a plan-first fix run
    can: ["Two independent fixes, each on its own branch", "Side-by-side comparison when both finish"],
    cannot: CANNOT_COMMON,
  },
];

// The flows the launcher offers.
export const LAUNCHABLE: FlowOption[] = FLOWS.filter((f) => !f.hidden);

export interface ModelOption {
  id: string;
  label: string;
  hint: string;
  bedrock: string;
  subscription: string;
}

// Bedrock profiles verified invokable in this account (docs/architecture/bedrock-auth.md).
// Implementation models. Every agent runs on the claude@foodics.com subscription
// by default; the Bedrock ids are only for runs that opt into Bedrock.
export const MODELS: ModelOption[] = [
  { id: "opus-5-5", label: "Opus 5.5", hint: "deepest", bedrock: "eu.anthropic.claude-opus-5-5", subscription: "claude-opus-5-5" },
  { id: "opus-4-7", label: "Opus 4.7", hint: "proven", bedrock: "eu.anthropic.claude-opus-4-7", subscription: "claude-opus-4-7[1m]" },
  { id: "sonnet-5-5", label: "Sonnet 5.5", hint: "faster", bedrock: "eu.anthropic.claude-sonnet-5-5", subscription: "claude-sonnet-5-5" },
  { id: "haiku-5-5", label: "Haiku 5.5", hint: "cheapest", bedrock: "eu.anthropic.claude-haiku-5-5", subscription: "claude-haiku-5-5" },
];

// The planning agent's model (a read-only investigator that hands the implementer a plan).
export const PLANNER_MODEL = "claude-fable-5-1";
const OTHER_LABELS: Record<string, string> = { "fable-5-1": "Fable 5.1", "fable-5": "Fable 5", "opus-4-6": "Opus 4.6", "sonnet-4-6": "Sonnet 4.6", "haiku-4-5": "Haiku 4.5" };

// "eu.anthropic.claude-opus-4-7" / "claude-opus-4-7[1m]" → "Opus 4.7"; unknown ids pass through.
export function modelLabel(id: string | null | undefined): string {
  if (!id) return "—";
  const m = MODELS.find((x) => x.bedrock === id || x.subscription === id || id.includes(x.id));
  if (m) return m.label;
  const other = Object.keys(OTHER_LABELS).find((k) => id.includes(k));
  return other ? OTHER_LABELS[other] : id;
}

export interface AgentConfig {
  model: string;
  auth: Auth;
  effort: Effort;
  review?: boolean;   // a second, read-only agent reviews the fix (flows with `review`)
  planning?: boolean; // a Fable planning agent investigates first (flows with `planner_model`); default on
  codegraph?: boolean; // code-map tools for the agent (flows with `disable_codegraph`); default on
}

export interface LaunchState {
  flow: FlowKind;
  ticket: string;
  jiraBaseUrl: string;
  githubHost: string;
  repo: string;
  branch: string;
  frontendRepo: string;
  frontendBranch: string;
  budget: number;
  maxTurns: number | null;
  timeoutSeconds: number | null;
  dryRun?: boolean; // run the whole team but write nothing to GitHub (flows with `dry_run`)
  a: AgentConfig;
  b: AgentConfig;
}

const EMPTY_OK = new Set(["planner_model"]);

export const TICKET_RE = /^[A-Z][A-Z0-9_]{1,19}-\d+$/;

function agentParams(c: AgentConfig): Record<string, unknown> {
  const m = MODELS.find((x) => x.id === c.model) ?? MODELS[0];
  const auth = c.auth === "bedrock"
    ? { use_bedrock: true, use_subscription: false, bedrock_model_id: m.bedrock }
    : { use_bedrock: false, use_subscription: true, model: m.subscription };
  return { ...auth, reasoning_effort: c.effort, review: !!c.review, disable_codegraph: c.codegraph === false,
    planner_model: c.planning === false ? "" : PLANNER_MODEL };
}

// Build the run params for one attempt, keeping only keys the flow's schema
// actually has (the API validates against it).
export function buildParams(s: LaunchState, schemaKeys: Set<string>, which: "a" | "b" = "a"): Record<string, unknown> {
  const p: Record<string, unknown> = {
    jira_base_url: s.jiraBaseUrl,
    jira_ticket_id: s.ticket,
    github_host: s.githubHost,
  };
  if (s.flow === "fullstack") {
    Object.assign(p, {
      github_repo_backend: s.repo, base_branch_backend: s.branch,
      github_repo_frontend: s.frontendRepo, base_branch_frontend: s.frontendBranch || "main",
    });
  } else {
    Object.assign(p, { github_repo: s.repo, base_branch: s.branch });
  }
  if (s.flow === "compare") {
    Object.assign(p, {
      claude_max_budget_usd: s.budget, codex_max_budget_usd: s.budget,
      claude_reasoning_effort: s.a.effort, codex_reasoning_effort: s.a.effort,
    });
    if (s.maxTurns) Object.assign(p, { claude_max_turns: s.maxTurns, codex_max_turns: s.maxTurns });
    if (s.timeoutSeconds) Object.assign(p, { claude_timeout_seconds: s.timeoutSeconds, codex_timeout_seconds: s.timeoutSeconds });
  } else {
    Object.assign(p, agentParams(which === "a" ? s.a : s.b), { max_budget_usd: s.budget });
    if (s.maxTurns) p.max_turns = s.maxTurns;
    if (s.timeoutSeconds) p.timeout_seconds = s.timeoutSeconds;
    if (s.dryRun) p.dry_run = true;
  }
  // Empty strings mean "use the flow's default", except where empty is a choice (no planner).
  return Object.fromEntries(Object.entries(p).filter(([k, v]) => schemaKeys.has(k) && v !== undefined && (v !== "" || EMPTY_OK.has(k))));
}

// What differs between attempt A and B, for the A/B labels.
export function variantLabel(c: AgentConfig): string {
  const m = MODELS.find((x) => x.id === c.model)?.label ?? c.model;
  return `${m} · ${c.effort}${c.auth === "subscription" ? " · subscription" : ""}${c.planning === false ? " · no planner" : ""}${c.review ? " · review" : ""}${c.codegraph === false ? " · no map" : ""}`;
}

export function validate(s: LaunchState): string | null {
  if (!TICKET_RE.test(s.ticket)) return "Enter a Jira key like DA-2260.";
  if (!s.repo) return "Pick the repository the agent should work on.";
  if (!s.branch) return "Pick the base branch.";
  if (s.flow === "fullstack" && !s.frontendRepo) return "Fullstack needs the frontend repository too.";
  return null;
}
